import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Crown, Send, Copy, X } from "lucide-react";

export default function OwnerInvites() {
  const [isOwner, setIsOwner] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/is_owner");
        setIsOwner(data.is_owner);
        if (data.is_owner) {
          const { data: list } = await api.get("/admin/invites");
          setItems(list);
        }
      } catch {}
    })();
  }, []);

  if (!isOwner) return null;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { email, name });
      toast.success(data.email_sent ? `Invite sent to ${email}` : `Invite created (email not sent — check config)`);
      setEmail(""); setName("");
      const { data: list } = await api.get("/admin/invites");
      setItems(list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const revoke = async (code) => {
    if (!confirm("Revoke this invite?")) return;
    try { await api.delete(`/admin/invites/${code}`); toast.info("Revoked"); const { data: list } = await api.get("/admin/invites"); setItems(list); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="mt-4 rounded-3xl bg-slate-900 text-white p-5 relative overflow-hidden" data-testid="owner-invites">
      <div className="absolute -right-6 -top-6 opacity-10"><Crown size={90} /></div>
      <div className="relative">
        <div className="flex items-center gap-2 text-emerald-300 text-[11px] uppercase tracking-widest font-bold"><Crown size={12} /> Owner tools</div>
        <div className="mt-1 font-manrope text-xl font-extrabold tracking-tight">Invite friends & family — 6 months free.</div>
        <p className="mt-1 text-sm text-slate-300 leading-relaxed">Send anyone a personal invite. They get 6 months of MoneyWise Premium, no card required.</p>

        <form onSubmit={send} className="mt-4 space-y-2">
          <input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="w-full bg-white/10 backdrop-blur border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-400" />
          <input data-testid="invite-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@example.com" className="w-full bg-white/10 backdrop-blur border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-400" />
          <button data-testid="invite-send-btn" type="submit" disabled={busy || !email} className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full py-3 font-semibold active:scale-[0.98] transition-transform duration-150 disabled:opacity-60 flex items-center justify-center gap-2">
            <Send size={14} /> {busy ? "Sending…" : "Send invite"}
          </button>
        </form>

        {items.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Sent invites · {items.length}</div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.code} className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-2" data-testid={`invite-${it.code}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{it.email}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold flex items-center gap-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${it.status === "redeemed" ? "bg-emerald-400" : it.status === "revoked" ? "bg-red-400" : "bg-amber-400"}`} />
                      {it.status} · {new Date(it.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={async () => { await navigator.clipboard.writeText(it.invite_url); toast.success("Link copied"); }}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    aria-label="Copy link"
                    data-testid={`invite-copy-${it.code}`}
                  ><Copy size={13} /></button>
                  {it.status === "pending" && (
                    <button onClick={() => revoke(it.code)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-red-500/30 flex items-center justify-center transition-colors" aria-label="Revoke" data-testid={`invite-revoke-${it.code}`}><X size={13} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
